/**
 * Chat Styles
 *
 * Shared inline style objects for the AI chat UI.
 * Follows the existing dark theme conventions.
 */

import type { CSSProperties } from 'react';

export const PANEL_WIDTH = 420;
export const MIN_PANEL_WIDTH = 320;
export const MAX_PANEL_WIDTH = 900;

export const colors = {
    bg: '#1e1e1e',
    bgLight: '#252526',
    bgInput: '#2a2a2a',
    border: '#444',
    borderLight: '#555',
    text: '#d4d4d4',
    textMuted: '#888',
    textBright: '#fff',
    accent: '#0078d4',
    accentHover: '#1a8ae8',
    error: '#ff5252',
    success: '#4caf50',
    userBubble: '#0078d4',
    toolBg: '#2d2d30',
};

export const chatContainer: CSSProperties = {
    position: 'fixed',
    bottom: 8,
    left: 8,
    width: PANEL_WIDTH,
    maxWidth: 'calc(100vw - 16px)',
    zIndex: 20,
    display: 'flex',
    flexDirection: 'column',
    pointerEvents: 'auto',
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    overflow: 'hidden',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.4)',
};

export const promptBar: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    background: colors.bg,
};

/** A file named on a sent message, or waiting in the prompt bar. */
export const attachmentChip: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    maxWidth: 220,
    padding: '2px 8px',
    background: colors.bgInput,
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    color: colors.text,
    fontSize: 11,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

export const promptInput: CSSProperties = {
    flex: 1,
    padding: '8px 12px',
    background: colors.bgInput,
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    color: colors.text,
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    resize: 'none',
};

export const sendButton: CSSProperties = {
    height: 36,
    padding: '0 16px',
    background: colors.accent,
    border: 'none',
    borderRadius: 6,
    color: colors.textBright,
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
    flexShrink: 0,
};

export const sendButtonDisabled: CSSProperties = {
    ...sendButton,
    opacity: 0.5,
    cursor: 'not-allowed',
};

export const stopButton: CSSProperties = {
    height: 36,
    width: 36,
    padding: 0,
    background: colors.error,
    border: 'none',
    borderRadius: 6,
    color: colors.textBright,
    cursor: 'pointer',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

export const iconButton: CSSProperties = {
    background: 'none',
    border: 'none',
    color: colors.textMuted,
    cursor: 'pointer',
    padding: 4,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

export const panelHeader: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    background: colors.bg,
    borderBottom: `1px solid ${colors.border}`,
    flexShrink: 0,
};

export const resizeHandle: CSSProperties = {
    height: 6,
    cursor: 'ns-resize',
    background: colors.bg,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

export const resizeHandleRight: CSSProperties = {
    position: 'absolute',
    top: 0,
    right: -3,
    width: 6,
    height: '100%',
    cursor: 'ew-resize',
    zIndex: 1,
};

export const resizeHandleCorner: CSSProperties = {
    position: 'absolute',
    top: -3,
    right: -3,
    width: 12,
    height: 12,
    cursor: 'nesw-resize',
    zIndex: 2,
};

export const resizeHandleBar: CSSProperties = {
    width: 32,
    height: 2,
    borderRadius: 1,
    background: colors.borderLight,
};

export const messagesContainer: CSSProperties = {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '8px 10px',
    background: colors.bg,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
};

export const userMessage: CSSProperties = {
    alignSelf: 'flex-end',
    background: colors.userBubble,
    color: colors.textBright,
    padding: '8px 12px',
    borderRadius: '12px 12px 2px 12px',
    fontSize: 13,
    maxWidth: '85%',
    wordBreak: 'break-word',
};

export const assistantMessage: CSSProperties = {
    alignSelf: 'flex-start',
    background: colors.bgLight,
    color: colors.text,
    padding: '8px 12px',
    borderRadius: '12px 12px 12px 2px',
    fontSize: 13,
    maxWidth: '90%',
    wordBreak: 'break-word',
    lineHeight: 1.5,
};

export const toolStatus: CSSProperties = {
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    padding: '4px 10px',
    background: colors.toolBg,
    borderRadius: 4,
    fontSize: 12,
    color: colors.textMuted,
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
};

export const codeBlock: CSSProperties = {
    background: '#1a1a1a',
    border: `1px solid ${colors.border}`,
    borderRadius: 4,
    padding: '8px 10px',
    fontSize: 12,
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    overflowX: 'auto',
    whiteSpace: 'pre',
    margin: '4px 0',
    color: colors.text,
};

export const settingsInput: CSSProperties = {
    ...promptInput,
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    fontSize: 12,
};

export const settingsLabel: CSSProperties = {
    fontSize: 12,
    color: colors.textMuted,
};

/** Generation controls row above the prompt bar (ChatPanel GenerationControls). */
export const pinBar: CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px 10px',
    padding: '6px 10px 0',
    background: colors.bg,
    borderTop: `1px solid ${colors.border}`,
    fontSize: 11,
    color: colors.textMuted,
    flexShrink: 0,
    userSelect: 'none',
};

export const pinField: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flex: '1 1 110px',
    minWidth: 0,
};

/** Same native select as the AI settings panel. */
export const pinSelect: CSSProperties = {
    ...settingsInput,
    flex: '1 1 0',
    minWidth: 0,
    padding: '2px 4px',
    cursor: 'pointer',
    appearance: 'auto',
    fontSize: 11,
};

export const pinToggle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    cursor: 'pointer',
};

export const smallButton: CSSProperties = {
    padding: '4px 12px',
    background: colors.bgInput,
    border: `1px solid ${colors.border}`,
    borderRadius: 4,
    color: colors.text,
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: 'inherit',
};

export const fab: CSSProperties = {
    position: 'fixed',
    bottom: 16,
    left: 16,
    width: 40,
    height: 40,
    borderRadius: 12,
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    color: colors.textMuted,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'auto',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.3)',
    transition: 'color 0.15s ease, border-color 0.15s ease',
};
